import type { AnyAction } from 'redux';

import type { LiteObject, PART_STATUSES } from '@frontend/api';
import type { PART_TYPES } from '@frontend/partviewer/src/constants';
import { UNITS } from '@frontend/partviewer/src/constants';
import type { Body } from '@frontend/partviewer/src/loaders/body';
import type { STEPBody } from '@frontend/partviewer/src/loaders/step/STEPBody';
import type { StepEdgeModel } from '@frontend/partviewer/src/loaders/step/StepEdgeModel';
import type { StepFaceModel } from '@frontend/partviewer/src/loaders/step/StepFaceModel';
import type {
  Style,
  StyleAdapter,
} from '@frontend/partviewer/src/loaders/step/StepStyleAdapter';
import type { StepVertexBody } from '@frontend/partviewer/src/loaders/step/StepVertexBody';
import type { Comparison } from '@frontend/partviewer/src/renderer/caliper-utils';
import type {
  UpdateVectorOperation,
  Selection,
  ForceView,
} from '@frontend/partviewer/src/renderer/renderer-component';
import type { ViewerDataResponseJson } from '@frontend/partviewer/src/viewerDataService';

import { types } from './actions';
import { EDITOR_MODES } from './constants';

const MAX_PAST_LENGTH = 50;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Store = any;

export type VectorizationStatus = keyof typeof PART_STATUSES | null;

export interface VectorEditorState {
  caliperToolSelections: Selection[];
  currentMeasurement: number | null;
  displayScale: number;
  nativeUnits: UNITS; //units defined in dxf (Should not change)
  displayUnits: UNITS; //units the user wants to interpret the DXF in
  editorMode: EDITOR_MODES;
  error: boolean;
  forceView: ForceView;
  hasExportVectors: boolean;
  historyUpdatedVectors: {
    past: {
      operation: UpdateVectorOperation[];
      inverse: UpdateVectorOperation[];
    }[]; // array of commands
    present: {
      operation: UpdateVectorOperation[];
      inverse: UpdateVectorOperation[];
    }; // single command
    future: {
      operation: UpdateVectorOperation[];
      inverse: UpdateVectorOperation[];
    }[]; // array of commands
  };
  isAssembly: boolean;
  isExportControlled: boolean;
  isFinalizing: boolean;
  isFinalized: boolean;
  isInitializing: boolean;
  isModalOpen: boolean;
  isSelectorToolActive: boolean;
  measurementComparison: Comparison | null;
  metaData: {
    options: {
      metaData:
        | (
            | StepVertexBody
            | StepEdgeModel
            | StepFaceModel
            | Body<PART_TYPES.STL>
          )['metaData']
        | Record<string, never>
        | undefined;
    };
  };
  partFileName: string | null;
  partFileUuid: string | null;
  partMasterVersion: string | null;
  partNumber: string | null;
  partUuid: string | null;
  saveFileName: string | null;
  selectorCumulativeInfo: Record<string, number>;
  selectorToolSelections: Selection[];
  setScale: boolean;
  stepBody: STEPBody | null;
  revision: string;
  thickness: number;
  updatedVectors: UpdateVectorOperation[];
  updatedVectorsCounter: number;
  vectorStyleByNameMap: Map<string, StyleAdapter>;
  originalVectorStyleByNameMap: Map<string, Style>;
  viewerData: ViewerDataResponseJson | null;
  dxf: Blob | null;
  vectorizationStatus: VectorizationStatus;
}

const initialState: () => VectorEditorState = () => ({
  caliperToolSelections: [],
  currentMeasurement: null,
  displayScale: 1.0,
  nativeUnits: UNITS.IN,
  displayUnits: UNITS.IN,
  editorMode: EDITOR_MODES.SCALE,
  error: false,
  forceView: null,
  hasExportVectors: false,
  historyUpdatedVectors: {
    past: [], // empty array of commands
    present: { operation: [], inverse: [] }, // actual empty command
    future: [], // empty array of commands
  },
  isAssembly: false,
  isExportControlled: false,
  isFinalizing: false,
  isFinalized: false,
  isInitializing: true,
  isModalOpen: false,
  isSelectorToolActive: true,
  measurementComparison: null,
  metaData: { options: { metaData: undefined } },
  partFileName: null,
  partFileUuid: null,
  partMasterVersion: null,
  partNumber: null,
  partUuid: null,
  saveFileName: null,
  selectorCumulativeInfo: {},
  selectorToolSelections: [],
  setScale: false,
  stepBody: null,
  revision: '',
  thickness: 0,
  updatedVectors: [],
  updatedVectorsCounter: 0,
  vectorStyleByNameMap: new Map(),
  originalVectorStyleByNameMap: new Map(),
  viewerData: null,
  dxf: null,
  vectorizationStatus: null,
});

// Undo/Redo logic follows the Command Pattern: https://en.wikipedia.org/wiki/Command_pattern
// This pattern captures commands/operations that affect the state -- the current operation + it's inverse
// Takes an operation and returns the operation + its inverse in an object to append to the currentHistory state

// an Operation would look like:

// can be 3 types:
//  1. toggleVectorVisibility
//    operation:   [{name: edgeName, visible: visibility}]
//    inverse:  [{name: edgeName, visible: !visibility}]

//  2. assignVectors
//    operation:   [{name: edgeName, toLayer: toLayerName, fromLayer: fromLayerName}]
//    inverse:  [{name: edgeName, toLayer: fromLayerName, fromLayer: toLayerName}]

//  3. deleteVectors
//    operation:   [{name: edgeName, visible: false, toLayer: "Paperless-Parts-Deleted", fromLayer: fromLayerName}]
//    inverse:  [{name: edgeName, visible: true, toLayer: fromLayerName, fromLayer: "Paperless-Parts-Deleted"}]

const createHistoryCommand = (operation: UpdateVectorOperation[]) => {
  const inverse: UpdateVectorOperation[] = operation.map((vectorOp) => {
    const vectorInv: UpdateVectorOperation = { name: vectorOp.name };
    if ('visible' in vectorOp) {
      vectorInv.visible = !vectorOp.visible;
    }
    if ('toLayer' in vectorOp && 'fromLayer' in vectorOp) {
      vectorInv.toLayer = vectorOp.fromLayer;
      vectorInv.fromLayer = vectorOp.toLayer;
    }
    return vectorInv;
  });

  return { operation, inverse };
};

// eslint-disable-next-line default-param-last
const reducer = (state = initialState(), action: AnyAction) => {
  const { payload, type } = action;
  switch (type) {
    case types.RENDERER_INITIALIZE_VECTOR_STYLE_BY_NAME_MAP: {
      // Create deep copy of vectorStyleByNameMap to save initial state -- used for returning vectors to their "original" layers
      const deepMap: Map<string, Style> = new Map(
        (function* () {
          for (const [key, obj] of (
            payload.obj.vectorStyleByNameMap as Map<string, StyleAdapter>
          ).entries()) {
            yield [key, obj.toJson()];
          }
        })(),
      );
      // Create reference of vectorStyleByNameMap in local state to vectorStyleByNameMap in STEPBody
      return {
        ...state,
        vectorStyleByNameMap: payload.obj.vectorStyleByNameMap as Map<
          string,
          StyleAdapter
        >,
        originalVectorStyleByNameMap: deepMap,
        stepBody: payload.obj,
      };
    }
    case types.EDITOR_RECEIVE_UPDATED_VECTORS: {
      const currentHistory = state.historyUpdatedVectors;
      const newHistory = createHistoryCommand(payload.updatedVectors);
      return {
        ...state,
        historyUpdatedVectors: {
          past:
            state.historyUpdatedVectors.past.length < MAX_PAST_LENGTH
              ? [...currentHistory.past, currentHistory.present]
              : [...currentHistory.past.slice(1), currentHistory.present],
          present: newHistory,
          future: [],
        },
      };
    }
    case types.EDITOR_INCREMENT_UPDATED_VECTORS_COUNTER: {
      // action to update vectors state passed down into renderer.ts
      return {
        ...state,
        updatedVectorsCounter: ++state.updatedVectorsCounter,
      };
    }
    case types.REDO: {
      const { past, present, future } = state.historyUpdatedVectors;
      return {
        ...state,
        historyUpdatedVectors: {
          past: [...past, present],
          present: future[0],
          future: future.slice(1),
        },
      };
    }
    case types.UNDO: {
      const { past, present, future } = state.historyUpdatedVectors;
      return {
        ...state,
        historyUpdatedVectors: {
          past: past.slice(0, past.length - 1),
          present: past[past.length - 1],
          future: [present, ...future],
        },
      };
    }
    case types.RENDERER_RECEIVE_UPDATED_VECTORS: {
      // action to update vectors state passed down into renderer.ts
      return {
        ...state,
        updatedVectors: payload.updatedVectors,
      };
    }
    case types.INIT_VECTOR_EDITOR:
      return {
        ...state,
        ...payload,
        isInitializing: false,
      };
    case types.EDITOR_RECEIVE_HAS_EXPORT_VECTORS:
      return {
        ...state,
        hasExportVectors: payload.hasExportVectors,
      };
    case types.MEASUREMENT_TOOL_ENABLE: {
      return {
        ...state,
        isSelectorToolActive: false,
      };
    }
    case types.MEASUREMENT_TOOL_RECEIVE_COMPARISON:
      return {
        ...state,
        measurementComparison: payload.comparison,
      };
    case types.MEASUREMENT_TOOL_RECEIVE_MEASUREMENT:
      return {
        ...state,
        currentMeasurement: payload.distance,
      };
    case types.MEASUREMENT_TOOL_RECEIVE_SELECTIONS:
      return {
        ...state,
        caliperToolSelections: payload.selections,
        currentMeasurement: null,
        measurementComparison: null,
      };
    case types.MEASUREMENT_TOOL_REMOVE_SELECTIONS:
      return {
        ...state,
        currentMeasurement: null,
        measurementComparison: null,
      };
    case types.SAVE_VECTOR_FILE_REQUEST:
      return {
        ...state,
        saveFileName: payload.saveFileName,
        isFinalizing: true,
      };
    case types.SAVE_VECTOR_FILE_SUCCESS:
      return {
        ...state,
        isFinalizing: false,
        isFinalized: true,
      };
    case types.SAVE_VECTOR_FILE_ERROR:
      return {
        ...state,
        isFinalizing: false,
      };
    case types.RECEIVE_UNITS:
      return {
        ...state,
        displayUnits: payload.displayUnits,
      };
    case types.RECEIVE_UNITS_SCALE_THICKNESS:
      return {
        ...state,
        displayScale: payload.displayScale,
        displayUnits: payload.displayUnits,
        thickness: payload.thickness,
      };
    case types.RECEIVE_EDITOR_MODE:
      // clear measurement tool and selector tool when changing to selector tool mode
      return {
        ...state,
        editorMode: payload.editorMode,
        caliperToolSelections: [],
        currentMeasurement: null,
        measurementComparison: null,
        selectorToolSelections: [],
        selectorCumulativeInfo: {},
      };
    case types.FINALIZE_SET_SCALE:
      return { ...state, setScale: true };
    case types.RECEIVE_FORCE_VIEW:
      return {
        ...state,
        forceView: payload.forceView,
      };
    case types.RECEIVE_META_DATA:
      return {
        ...state,
        metaData: payload.metaData,
      };
    case types.RECEIVE_VIEWER_DATA:
      return {
        ...state,
        viewerData: payload.viewerData,
      };
    case types.RESET:
      return {
        ...initialState(),
      };
    case types.SELECTOR_TOOL_BOX_SELECT:
      return {
        ...state,
        selectorToolSelections: [
          ...state.selectorToolSelections,
          ...payload.selections,
        ],
      };
    case types.SELECTOR_TOOL_CLEAR_ALL_SELECTIONS:
      return {
        ...state,
        selectorToolSelections: [],
      };
    case types.SELECTOR_TOOL_ENABLE: {
      return {
        ...state,
        isSelectorToolActive: true,
      };
    }
    case types.SELECTOR_TOOL_RECEIVE_CUMULATIVE_INFO:
      return {
        ...state,
        selectorCumulativeInfo: payload.cumulativeInfo,
      };
    // Multiple selections received via SetLayersPanel onSelect call
    case types.SELECTOR_TOOL_RECEIVE_SELECTIONS: {
      return {
        ...state,
        selectorToolSelections: state.selectorToolSelections.concat(
          payload.selections,
        ),
      };
    }
    case types.SELECTOR_TOOL_REMOVE_SELECTIONS: {
      const selectorToolSelections = state.selectorToolSelections.filter(
        ({ selectedObject }) =>
          !(payload.objs as LiteObject['obj'][]).includes(selectedObject.obj),
      );
      return {
        ...state,
        selectorToolSelections,
      };
    }
    case types.SELECTOR_TOOL_RECEIVE_SELECTION: {
      // if setting scale, selector tool only supports one selection
      if (state.editorMode === EDITOR_MODES.SCALE) {
        return {
          ...state,
          selectorToolSelections: [payload.selection],
        };
      }
      // normal mode
      return {
        ...state,
        selectorToolSelections: state.selectorToolSelections.concat([
          payload.selection,
        ]),
      };
    }
    case types.SELECTOR_TOOL_REMOVE_SELECTION: {
      const selectorToolSelections = state.selectorToolSelections.filter(
        ({ selectedObject }) =>
          selectedObject.obj !== (payload.obj as LiteObject['obj']),
      );
      return {
        ...state,
        selectorToolSelections,
      };
    }
    case types.RENDERER_RECEIVE_SERIALIZED_DXF:
      return {
        ...state,
        dxf: payload.dxf,
      };
    case types.SAVE_VECTOR_FILE_IS_MODAL_OPEN:
      return {
        ...state,
        isModalOpen: true,
      };
    case types.SAVE_VECTOR_FILE_IS_MODAL_CLOSED:
      return {
        ...state,
        isModalOpen: false,
      };
    case types.RECEIVE_VECTORIZATION_STATUS:
      return {
        ...state,
        vectorizationStatus: payload.status,
      };
    case types.RENDERER_RECEIVE_ERROR:
      return {
        ...state,
        error: true,
        isInitializing: false,
      };

    default:
      return state;
  }
};

export default reducer;

const getScope = (state: Store): VectorEditorState =>
  state.modules.vectorEditor.app.page;

export const getCaliperToolSelections = (state: Store) =>
  getScope(state).caliperToolSelections;

export const getCurrentMeasurementValue = (state: Store) =>
  getScope(state).currentMeasurement;

export const getDisplayScale = (state: Store) => getScope(state).displayScale;

export const getRevision = (state: Store) => getScope(state).revision;

export const getIsAssembly = (state: Store) => getScope(state).isAssembly;

export const getIsExportControlled = (state: Store) =>
  getScope(state).isExportControlled;

export const getIsInitializing = (state: Store) =>
  getScope(state).isInitializing;

export const getIsFinalizing = (state: Store) => getScope(state).isFinalizing;

export const getDisplayUnits = (state: Store) => getScope(state).displayUnits;

export const getNativeUnits = (state: Store) => getScope(state).nativeUnits;

export const getThickness = (state: Store) => getScope(state).thickness;

export const getEditorMode = (state: Store) => getScope(state).editorMode;

export const getForceView = (state: Store) => getScope(state).forceView;

export const getHasExportVectors = (state: Store) =>
  getScope(state).hasExportVectors;

export const getPresentHistoryUpdatedVectors = (state: Store) =>
  getScope(state).historyUpdatedVectors.present;

export const getFutureHistoryUpdatedVectors = (state: Store) =>
  getScope(state).historyUpdatedVectors.future[0];

export const getUpdatedVectors = (state: Store) =>
  getScope(state).updatedVectors;

export const getCanUndo = (state: Store) =>
  getScope(state).historyUpdatedVectors.past.length > 0;

export const getCanRedo = (state: Store) =>
  getScope(state).historyUpdatedVectors.future.length > 0;

export const getIsMeasurementToolActive = (state: Store) =>
  !getScope(state).isSelectorToolActive;

export const getIsSelectorToolActive = (state: Store) =>
  getScope(state).isSelectorToolActive;

export const getMeasurementComparison = (state: Store) =>
  getScope(state).measurementComparison;

export const getMetaData = (state: Store) => getScope(state).metaData;

export const getPartFileUuid = (state: Store) => getScope(state).partFileUuid;

export const getSelectorCumulativeInfo = (state: Store) =>
  getScope(state).selectorCumulativeInfo;

export const getSelectorToolSelections = (state: Store) =>
  getScope(state).selectorToolSelections;

export const getSetScale = (state: Store) => getScope(state).setScale;

export const getIsFinalized = (state: Store) => getScope(state).isFinalized;

export const getViewerData = (state: Store) => getScope(state).viewerData;

export const getPartFileName = (state: Store) => getScope(state).partFileName;

export const getPartNumber = (state: Store) => getScope(state).partNumber;

export const getPartUuid = (state: Store) => getScope(state).partUuid;

export const getPartMasterVersion = (state: Store) =>
  getScope(state).partMasterVersion;

export const getVectorStyleByNameMap = (state: Store) =>
  getScope(state).vectorStyleByNameMap;

export const getOriginalVectorStyleByNameMap = (state: Store) =>
  getScope(state).originalVectorStyleByNameMap;

export const getDXF = (state: Store) => getScope(state).dxf;

export const getStepBody = (state: Store) => getScope(state).stepBody;

export const getIsModalOpen = (state: Store) => getScope(state).isModalOpen;

export const getSaveFileName = (state: Store) => getScope(state).saveFileName;

export const getUpdatedVectorsCounter = (state: Store) =>
  getScope(state).updatedVectorsCounter;

export const getVectorizationStatus = (state: Store) =>
  getScope(state).vectorizationStatus;

export const getError = (state: Store) => getScope(state).error;
